import { Dispatch } from "redux";
import axios from "axios";
import { getMarketAreas } from "api/getMarketAreas";
import { getMarketAreaSettings } from "api/getMarketAreaSettings";
import { getMarketAreasAvailable } from "api/getMarketAreasAvailable";
import {
  patchMarketAreaSettings,
  IPatchMarketAreaSettingsPayload,
} from "api/patchMarketAreaSettings";
import {
  createMarketArea as createMarketAreaApi,
  ICreateMarketAreaPayload,
} from "api/createMarketArea";
import { archiveMarketAreas as archiveMarketAreasApi } from "api/archiveMarketAreas";
import { unarchiveMarketAreas as unarchiveMarketAreasApi } from "api/unarchiveMarketAreas";
import { deleteMarketAreas as deleteMarketAreasApi } from "api/deleteMarketAreas";
import { analyzeMarketArea as analyzeMarketAreaApi } from "api/analyzeMarketArea";
import { editMarketAreaById, IEditMarketArea } from "api/editMarketAreaById";
import {
  replaceMarketAreaById,
  IReplaceMarketAreaPayload,
} from "api/replaceMarketAreaById";
import { ReduxState, TypedDispatch } from "store";
import {
  getTableSortFilterByTableName,
  resetFiltersByTableName,
  TABLE_TABS_ENUM,
} from "features/tableFilters";
import { setSuccessModalOpen } from "features/SignIn/SignInActions";
import { isAdmin } from "config/constants";
import { Actions } from "./interfaces";
import { handleThunkError } from "./helpers/handleThunkError";
import {
  IMarketAreasInfo,
  IMarketAreaSettings,
  MarketAreaStatus,
  TMarketArea,
} from "./types";

export const SET_MARKET_AREAS_LOADING = "SET_MARKET_AREAS_LOADING";
export const SET_MARKET_AREAS_INFO = "SET_MARKET_AREAS_INFO";
export const SET_SEARCHED_MARKET_AREA_NAME = "SET_SEARCHED_MARKET_AREA_NAME";
export const SET_MARKET_AREA_SETTINGS = "SET_MARKET_AREA_SETTINGS";
export const SET_MARKET_AREA_SETTINGS_LOADING =
  "SET_MARKET_AREA_SETTINGS_LOADING";
export const SET_MARKET_AREAS_AVAILABLE = "SET_MARKET_AREAS_AVAILABLE";

export const setMarketAreasLoading = (value: boolean): Actions => ({
  type: SET_MARKET_AREAS_LOADING,
  payload: value,
});

export const setMarketAreasInfo = (value: IMarketAreasInfo): Actions => ({
  type: SET_MARKET_AREAS_INFO,
  payload: value,
});

export const setSearchedMarketAreaName = (value: string): Actions => ({
  type: SET_SEARCHED_MARKET_AREA_NAME,
  payload: value,
});

export const setMarketAreaSettings = (
  value: IMarketAreaSettings | null
): Actions => ({
  type: SET_MARKET_AREA_SETTINGS,
  payload: value,
});

export const setMarketAreaSettingsLoading = (value: boolean): Actions => ({
  type: SET_MARKET_AREA_SETTINGS_LOADING,
  payload: value,
});

export const setMarketAreasAvailable = (value: boolean | null): Actions => ({
  type: SET_MARKET_AREAS_AVAILABLE,
  payload: value,
});

export const fetchMarketAreasAvailability =
  () => async (dispatch: TypedDispatch, getState: () => ReduxState) => {
    const state = getState();
    if (isAdmin(state.SignInReducer.userInfo.roles)) return;
    if (state.MarketAreasReducer.isAvailable !== null) return;
    try {
      const response = await getMarketAreasAvailable();
      dispatch(setMarketAreasAvailable(response));
    } catch (error) {
      if (axios.isCancel(error)) return;
      dispatch(setMarketAreasAvailable(false));
    }
  };

export const fetchMarketAreaSettings =
  () => async (dispatch: TypedDispatch) => {
    dispatch(setMarketAreaSettingsLoading(true));
    try {
      const response = await getMarketAreaSettings();
      dispatch(setMarketAreaSettings(response));
    } catch (error) {
      handleThunkError(
        error,
        "Failed to load market area settings. Please try again."
      );
    } finally {
      dispatch(setMarketAreaSettingsLoading(false));
    }
  };

export const updateMarketAreaSettings =
  (payload: IPatchMarketAreaSettingsPayload) =>
  async (dispatch: TypedDispatch) => {
    try {
      const response = await patchMarketAreaSettings(payload);
      dispatch(setMarketAreaSettings(response));
      dispatch(setSuccessModalOpen(true));
    } catch (error) {
      handleThunkError(
        error,
        "Failed to update market area settings. Please try again."
      );
    }
  };

export const resetMarketAreasFilters = () => (dispatch: Dispatch) => {
  dispatch(setSearchedMarketAreaName(""));
  dispatch(resetFiltersByTableName(TABLE_TABS_ENUM.ACTIVE_MARKET_AREAS));
  dispatch(resetFiltersByTableName(TABLE_TABS_ENUM.ARCHIVE_MARKET_AREAS));
};

export const fetchMarketAreas =
  (status?: MarketAreaStatus, signal?: AbortSignal) =>
  async (dispatch: Dispatch, getState: () => ReduxState) => {
    dispatch(setMarketAreasLoading(true));
    try {
      const state = getState();
      const tableName =
        status === MarketAreaStatus.ARCHIVED
          ? TABLE_TABS_ENUM.ARCHIVE_MARKET_AREAS
          : TABLE_TABS_ENUM.ACTIVE_MARKET_AREAS;
      const { sortField, sortOrder } = getTableSortFilterByTableName(
        state,
        tableName
      );
      const response = await getMarketAreas(
        {
          status,
          name: state.MarketAreasReducer.searchedName || undefined,
          sortField,
          sortOrder,
        },
        signal
      );
      dispatch(setMarketAreasInfo(response));
    } catch (error) {
      handleThunkError(error, "Failed to load market areas. Please try again.");
    } finally {
      if (!signal?.aborted) {
        dispatch(setMarketAreasLoading(false));
      }
    }
  };

export const updateMarketArea =
  (id: string, payload: IEditMarketArea, status: MarketAreaStatus) =>
  async (dispatch: TypedDispatch) => {
    try {
      await editMarketAreaById(id, payload);
      dispatch(fetchMarketAreas(status));
    } catch (error) {
      handleThunkError(error);
    }
  };

export const bulkArchiveMarketAreas =
  (ids: string[]) => async (dispatch: TypedDispatch) => {
    if (!ids.length) return;
    try {
      await archiveMarketAreasApi(ids);
      dispatch(fetchMarketAreas(MarketAreaStatus.ACTIVE));
    } catch (error) {
      handleThunkError(
        error,
        "Failed to archive market areas. Please try again."
      );
    }
  };

export const bulkUnarchiveMarketAreas =
  (ids: string[]) => async (dispatch: TypedDispatch) => {
    if (!ids.length) return;
    try {
      await unarchiveMarketAreasApi(ids);
      dispatch(fetchMarketAreas(MarketAreaStatus.ARCHIVED));
    } catch (error) {
      handleThunkError(
        error,
        "Failed to unarchive market areas. Please try again."
      );
    }
  };

export const bulkDeleteMarketAreas =
  (ids: string[], status: MarketAreaStatus) =>
  async (dispatch: TypedDispatch) => {
    if (!ids.length) return;
    try {
      await deleteMarketAreasApi(ids);
      dispatch(fetchMarketAreas(status));
    } catch (error) {
      handleThunkError(
        error,
        "Failed to remove market areas. Please try again."
      );
    }
  };

export const createMarketArea =
  (payload: ICreateMarketAreaPayload) => async (): Promise<boolean> => {
    try {
      await createMarketAreaApi(payload);
      return true;
    } catch (error) {
      handleThunkError(
        error,
        "Failed to create market area. Please try again."
      );
      return false;
    }
  };

export const replaceMarketArea =
  (id: string, payload: IReplaceMarketAreaPayload) =>
  async (): Promise<TMarketArea | null> => {
    try {
      return await replaceMarketAreaById(id, payload);
    } catch (error) {
      handleThunkError(
        error,
        "Failed to update market area. Please try again."
      );
      return null;
    }
  };

export const archiveMarketArea = (id: string) => async (): Promise<boolean> => {
  try {
    await archiveMarketAreasApi([id]);
    return true;
  } catch (error) {
    handleThunkError(error, "Failed to archive market area. Please try again.");
    return false;
  }
};

export const unarchiveMarketArea =
  (id: string) => async (): Promise<boolean> => {
    try {
      await unarchiveMarketAreasApi([id]);
      return true;
    } catch (error) {
      handleThunkError(
        error,
        "Failed to unarchive market area. Please try again."
      );
      return false;
    }
  };

export const deleteMarketArea = (id: string) => async (): Promise<boolean> => {
  try {
    await deleteMarketAreasApi([id]);
    return true;
  } catch (error) {
    handleThunkError(error, "Failed to remove market area. Please try again.");
    return false;
  }
};

export const analyzeMarketArea = (id: string) => async () => {
  try {
    await analyzeMarketAreaApi(id);
  } catch (error) {
    handleThunkError(
      error,
      "Failed to start market area analysis. Please try again."
    );
  }
};
